import React, { useState, useEffect } from 'react';
import { Stack, Tab, Tabs } from 'react-bootstrap';
import { useActiveJobMutation, useExpiredJobListMutation, useInactiveJobListQuery } from 'services';
import PaginationBox from 'components/PaginationBox';
import InactiveExpireJobListing from 'components/Common/Jobs/InactiveExpireJobListing';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { dataLimits } from 'constants/index';
import JobCardSkeleton from 'components/Common/Skeleton/JobCardSkeleton';
import useSkeleton from 'hooks/useSkeleton';
import useTranslate from 'hooks/useTranslate';
import './styles.scss';

const orderBy = 'createdAt';
const order = 'DESC';

const InactiveJobs: React.FC = () => {
	const { translate } = useTranslate();
	const queryClient = useQueryClient();
	const [currentPage, setCurrentPage] = useState(1);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const delayedLoading = useSkeleton(isLoading);
	const location = useLocation();
	const navigate = useNavigate();
	const [inactiveQuery, setInactiveQuery] = useState({
		page: 1,
		limit: dataLimits.inActiveListing,
		inActive: true,
	});

	const [expiredJobQuery, setExpiredJobQuery] = useState({
		page: 1,
		limit: dataLimits.inActiveListing,
		hasExpired: true,
	});

	const { data: { docs: jobs = [], meta = {} } = [], refetch } =
		useInactiveJobListQuery(inactiveQuery);
	const { limit = 2, totalDocs } = meta;
	const [expiredJobMeta, setExpiredJobMeta] = useState<any>({});
	const { mutateAsync: getExpiredJobListMutation } = useExpiredJobListMutation();
	const { mutateAsync: jobActiveMutation } = useActiveJobMutation();

	const defaultTab = location?.pathname.includes('inactive-jobs') ? 'inActive' : 'history';

	useEffect(() => {
		getInactiveListData();
	}, [inactiveQuery.page, orderBy, order]);

	useEffect(() => {
		getExpiredJobListData();
	}, [expiredJobQuery.page, orderBy, order]);

	const setCurrentPageFunctionInactive = (pageNum: number) => {
		window.scrollTo({
			top: 0,
			behavior: 'smooth',
		});
		setInactiveQuery((prevValues) => ({ ...prevValues, page: pageNum }));
	};
	const setCurrentPageFunctionExpired = (pageNum: number) => {
		window.scrollTo({
			top: 0,
			behavior: 'smooth',
		});
		setExpiredJobQuery((prevValues) => ({ ...prevValues, page: pageNum }));
	};

	const getInactiveListData = () => {
		setIsLoading(true);
		queryClient.invalidateQueries({ queryKey: ['inactive-job'] }).finally(() => {
			setIsLoading(false);
		});
	};

	const getExpiredJobListData = () => {
		setIsLoading(true);
		getExpiredJobListMutation(expiredJobQuery)
			.then(() => {})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const handleTabChange = (k: any) => {
		setInactiveQuery((prevValues) => ({ ...prevValues, page: 1 }));
		if (['inActive'].includes(k)) {
			navigate('/company/inactive-jobs');
			getInactiveListData();
		} else {
			navigate('/company/expired-jobs');
			getExpiredJobListData();
		}
	};

	const activeJob = (id: any) => {
		setIsLoading(true);
		jobActiveMutation(id)
			.then((res: any) => {
				if (res?.success) {
					getExpiredJobListData();
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	return (
		<div className="right-area flex-1 d-flex flex-column">
			<div className="white-box bg-white">
				<Stack className="gap-4">
					<div className="jobs-filter d-flex align-items-center justify-content-between">
						<p className="fw-semibold mb-0">{translate('Company.inactiveJobs')}</p>
					</div>
					<div className="connections-tab">
						<Tabs
							defaultActiveKey={defaultTab}
							id="uncontrolled-tab-example"
							onSelect={(k: any) => {
								handleTabChange(k);
							}}
						>
							<Tab eventKey="inActive" title={translate('Common.inactive')}>
								{delayedLoading ? (
									<JobCardSkeleton />
								) : (
									<>
										{jobs?.length > 0 ? (
											jobs.map((job: any) => (
												<InactiveExpireJobListing
													job={job}
													key={`Inactive-${job?.companyId}-${job?.title}-job`}
													refetch={refetch}
													dataLength={jobs?.length}
													currentPage={currentPage}
													setCurrentPage={setCurrentPage}
													enableFavBtn={false}
													isRenew={false}
													startDate={job.createdAt}
													endDate={job.expireOn}
													setInactive={activeJob}
												/>
											))
										) : (
											<Stack className="posted-job-box">
												<p className="mb-0">{translate('Common.noJobsFound')}</p>
											</Stack>
										)}
										{totalDocs > limit && (
											<PaginationBox
												totalData={totalDocs}
												limit={limit}
												page={inactiveQuery.page}
												setCurrentPage={setCurrentPageFunctionInactive}
											/>
										)}
									</>
								)}
							</Tab>
						</Tabs>
					</div>
				</Stack>
			</div>
		</div>
	);
};

export default InactiveJobs;
